import { exportKey, importKey } from './crypto.js';

/**
 * Local conversation-key storage (concept §5).
 *
 * The accepted V1 limitation this implements: a conversation is readable only
 * in the **browser profile that created or joined it**. There is no cross-device
 * recovery, and none is emulated by shipping the key anywhere.
 *
 * IndexedDB is used rather than localStorage because it survives longer under
 * browser storage pressure and can be requested as persistent.
 *
 * The other rule the artifact fixes: the product must **not silently create a
 * link whose key cannot be retained**. {@link checkKeyStorage} is what the
 * invitation flow calls first, and it blocks creation when storage is
 * unavailable.
 */

const DB_NAME = 'kairelo';
const DB_VERSION = 1;
const STORE = 'conversation-keys';

export type KeyStorageState =
  | { available: true; persisted: boolean; mechanism: 'indexeddb' }
  | { available: false; reason: 'no-indexeddb' | 'blocked' | 'no-crypto'; detail: string };

interface StoredKey {
  conversationId: string;
  raw: ArrayBuffer;
  role: 'owner' | 'visitor';
  createdAt: number;
  expiresAt: number | null;
  /**
   * The full invitation URL, fragment included, for links this browser created.
   *
   * The server returns the invitation token exactly once, to the creating
   * browser, and never lists it again — it is a bearer credential. Without a
   * local copy an owner who closed the dialog has no way to re-share a link
   * they have not yet sent to anyone.
   *
   * Keeping it here weakens nothing: this record already holds the conversation
   * key, which is the more sensitive half, and it never leaves the browser.
   * Records written by earlier builds simply lack the field.
   */
  invitationUrl?: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser context.'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'conversationId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open local storage.'));
    request.onblocked = () => reject(new Error('Local storage is blocked by another tab.'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = fn(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Local storage request failed.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Local storage aborted.'));
    });
  } finally {
    db.close();
  }
}

/**
 * Probes whether this browser profile can actually retain a conversation key.
 *
 * Performs a real write-read-delete rather than a feature check, because
 * private-browsing modes commonly expose the IndexedDB API and then fail on
 * use. Also asks for persistent storage, which reduces the chance of eviction.
 */
export async function checkKeyStorage(): Promise<KeyStorageState> {
  if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') {
    return {
      available: false,
      reason: 'no-crypto',
      detail:
        'This browser does not provide the Web Crypto API, which is required to encrypt conversations.',
    };
  }
  if (typeof indexedDB === 'undefined') {
    return {
      available: false,
      reason: 'no-indexeddb',
      detail:
        'This browser has no local database available, so a conversation key could not be kept here.',
    };
  }

  const probeId = `__storage-probe-${Math.random().toString(36).slice(2)}`;
  try {
    await withStore('readwrite', (store) =>
      store.put({
        conversationId: probeId,
        raw: new ArrayBuffer(8),
        role: 'owner',
        createdAt: Date.now(),
        expiresAt: null,
      } satisfies StoredKey),
    );
    const readBack = await withStore<StoredKey | undefined>('readonly', (store) =>
      store.get(probeId),
    );
    await withStore('readwrite', (store) => store.delete(probeId));

    if (!readBack) {
      return {
        available: false,
        reason: 'blocked',
        detail:
          'This browser accepted a write to local storage but could not read it back. Private browsing is the usual cause.',
      };
    }
  } catch (error) {
    return {
      available: false,
      reason: 'blocked',
      detail:
        error instanceof Error && error.name === 'SecurityError'
          ? 'Local storage is blocked by this browser’s privacy settings.'
          : 'Local storage could not be used in this browser profile. Private browsing is the usual cause.',
    };
  }

  // Best-effort request for persistent storage so the key is not evicted under
  // pressure. A refusal is not fatal: the key still works for this session.
  let persisted = false;
  try {
    if (navigator.storage?.persisted) {
      persisted = await navigator.storage.persisted();
      if (!persisted && navigator.storage.persist) {
        persisted = await navigator.storage.persist();
      }
    }
  } catch {
    persisted = false;
  }

  return { available: true, persisted, mechanism: 'indexeddb' };
}

export async function storeConversationKey(params: {
  conversationId: string;
  key: CryptoKey;
  role: 'owner' | 'visitor';
  expiresAt: number | null;
  invitationUrl?: string;
}): Promise<void> {
  const raw = await exportKey(params.key);
  await withStore('readwrite', (store) =>
    store.put({
      conversationId: params.conversationId,
      // Copy into a fresh ArrayBuffer: structured clone of a view can carry
      // the whole underlying buffer.
      raw: raw.slice().buffer,
      role: params.role,
      createdAt: Date.now(),
      expiresAt: params.expiresAt,
      ...(params.invitationUrl ? { invitationUrl: params.invitationUrl } : {}),
    } satisfies StoredKey),
  );
}

/**
 * Invitation URLs this browser created, by conversation.
 *
 * Only useful while a link is still unclaimed; once someone joins, the link is
 * spent and the list stops offering it.
 */
export async function loadInvitationUrls(): Promise<Map<string, string>> {
  try {
    const all = await withStore<StoredKey[]>('readonly', (store) => store.getAll());
    return new Map(
      all
        .filter((entry): entry is StoredKey & { invitationUrl: string } =>
          Boolean(entry.invitationUrl),
        )
        .map((entry) => [entry.conversationId, entry.invitationUrl]),
    );
  } catch {
    return new Map();
  }
}

/** Returns the local key for a conversation, or null when this profile has none. */
export async function loadConversationKey(conversationId: string): Promise<CryptoKey | null> {
  try {
    const stored = await withStore<StoredKey | undefined>('readonly', (store) =>
      store.get(conversationId),
    );
    if (!stored) return null;
    return importKey(new Uint8Array(stored.raw));
  } catch {
    return null;
  }
}

/**
 * Removes a local key.
 *
 * Called when a client observes expiry: the artifact asks clients to drop their
 * stored key on a best-effort basis at that point (concept §7).
 */
export async function forgetConversationKey(conversationId: string): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.delete(conversationId));
  } catch {
    // Best-effort by design.
  }
}

/** Drops keys for conversations whose recorded expiry has passed. */
export async function forgetExpiredKeys(nowMs = Date.now()): Promise<number> {
  try {
    const all = await withStore<StoredKey[]>('readonly', (store) => store.getAll());
    const stale = all.filter((entry) => entry.expiresAt !== null && entry.expiresAt <= nowMs);
    for (const entry of stale) await forgetConversationKey(entry.conversationId);
    return stale.length;
  } catch {
    return 0;
  }
}

export async function listStoredConversationIds(): Promise<string[]> {
  try {
    const all = await withStore<StoredKey[]>('readonly', (store) => store.getAll());
    return all.map((entry) => entry.conversationId);
  } catch {
    return [];
  }
}
